import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PlayCircle as PlayIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import apiClient from '../services/api';

interface RecordedSession {
  id: string;
  title: string;
  teacher_name: string;
  subject: string;
  grade: string;
  board: string;
  vimeo_video_id: string;
  duration: number;
  views: number;
  is_published: boolean;
  created_at: string;
  concepts_count: number;
  resources_count: number;
  tests_count: number;
}

const RecordedSessionsPage: React.FC = () => {
  const [sessions, setSessions] = useState<RecordedSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSession, setEditingSession] = useState<RecordedSession | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [resourceUploading, setResourceUploading] = useState<Record<number, boolean>>({});
  const [resourcePreviews, setResourcePreviews] = useState<Record<number, string>>({});

  const isUploadedKey = (url?: string) => !!url && !/^https?:\/\//i.test(url);

  const resolvePreview = async (key: string): Promise<string> => {
    if (!isUploadedKey(key)) return key;
    const { data } = await apiClient.get('/admin/upload/resolve', { params: { key } });
    return data.data.url;
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    teacher_name: '',
    subject: '',
    grade: '',
    board: '',
    vimeo_video_id: '',
    thumbnail_url: '',
    duration: 0,
    is_published: false,
    tags: [] as string[],
    concepts: [] as Array<{ title: string; description: string }>,
    resources: [] as Array<{ title: string; type: string; url: string }>,
    practiceTestIds: [] as string[],
  });

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      // Admin endpoint: raw content_url/thumbnail_url/resource keys (safe to
      // re-save) and includes unpublished drafts, unlike the public listing.
      const response = await apiClient.get('/recorded-sessions/admin/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSnackbar({ open: true, message: 'Error fetching sessions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (session?: RecordedSession) => {
    setThumbnailPreview('');
    setResourcePreviews({});
    if (session) {
      setEditingSession(session);
      try {
        // The list only carries counts, not full description/thumbnail/
        // concepts/resources/practiceTests — fetch the full record so
        // editing doesn't silently wipe them on save.
        const { data: full } = await apiClient.get(`/recorded-sessions/admin/sessions/${session.id}`);
        const resources = (full.resources || []).map((r: any) => ({ title: r.title, type: r.type, url: r.url }));
        setFormData({
          title: full.title,
          description: full.description || '',
          teacher_name: full.teacher_name,
          subject: full.subject,
          grade: full.grade,
          board: full.board,
          vimeo_video_id: full.vimeo_video_id,
          thumbnail_url: full.thumbnail_url || '',
          duration: full.duration,
          is_published: full.is_published,
          tags: full.tags || [],
          concepts: (full.concepts || []).map((c: any) => ({ title: c.title, description: c.description })),
          resources,
          practiceTestIds: (full.practiceTests || []).map((t: any) => t.id),
        });

        if (full.thumbnail_url) {
          resolvePreview(full.thumbnail_url).then(setThumbnailPreview).catch(() => {});
        }
        resources.forEach((r: { url: string }, i: number) => {
          if (r.url) resolvePreview(r.url).then(url => setResourcePreviews(prev => ({ ...prev, [i]: url }))).catch(() => {});
        });
      } catch (error) {
        console.error('Error loading session details:', error);
        setSnackbar({ open: true, message: 'Failed to load session details', severity: 'error' });
      }
    } else {
      setEditingSession(null);
      setFormData({
        title: '',
        description: '',
        teacher_name: '',
        subject: '',
        grade: '',
        board: '',
        vimeo_video_id: '',
        thumbnail_url: '',
        duration: 0,
        is_published: false,
        tags: [],
        concepts: [],
        resources: [],
        practiceTestIds: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSession(null);
  };

  const handleSave = async () => {
    try {
      if (editingSession) {
        await apiClient.put(`/recorded-sessions/sessions/${editingSession.id}`, formData);
        setSnackbar({ open: true, message: 'Session updated successfully', severity: 'success' });
      } else {
        await apiClient.post('/recorded-sessions/sessions', formData);
        setSnackbar({ open: true, message: 'Session created successfully', severity: 'success' });
      }
      handleCloseDialog();
      fetchSessions();
    } catch (error) {
      console.error('Error saving session:', error);
      setSnackbar({ open: true, message: 'Error saving session', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    try {
      await apiClient.delete(`/recorded-sessions/sessions/${id}`);
      setSnackbar({ open: true, message: 'Session deleted successfully', severity: 'success' });
      fetchSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      setSnackbar({ open: true, message: 'Error deleting session', severity: 'error' });
    }
  };

  const handleAddConcept = () => {
    setFormData({
      ...formData,
      concepts: [...formData.concepts, { title: '', description: '' }],
    });
  };

  const handleAddResource = () => {
    setFormData({
      ...formData,
      resources: [...formData.resources, { title: '', type: 'pdf', url: '' }],
    });
  };

  const uploadThumbnail = async (file: File) => {
    setThumbnailUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await apiClient.post('/admin/upload/image?folder=recorded-sessions', body, {
        headers: { 'Content-Type': undefined },
      });
      setFormData(prev => ({ ...prev, thumbnail_url: data.data.key }));
      setThumbnailPreview(data.data.url);
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      setSnackbar({ open: true, message: 'Thumbnail upload failed', severity: 'error' });
    } finally {
      setThumbnailUploading(false);
    }
  };

  const uploadResourceFile = async (index: number, file: File) => {
    setResourceUploading(prev => ({ ...prev, [index]: true }));
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await apiClient.post('/admin/upload/pdf?folder=recorded-sessions', body, {
        headers: { 'Content-Type': undefined },
      });
      const newResources = [...formData.resources];
      newResources[index] = { ...newResources[index], url: data.data.key };
      setFormData({ ...formData, resources: newResources });
      setResourcePreviews(prev => ({ ...prev, [index]: data.data.url }));
    } catch (error) {
      console.error('Error uploading resource file:', error);
      setSnackbar({ open: true, message: 'File upload failed', severity: 'error' });
    } finally {
      setResourceUploading(prev => ({ ...prev, [index]: false }));
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Recorded Sessions Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add New Session
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Teacher</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Content</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.id}>
                <TableCell>{session.title}</TableCell>
                <TableCell>{session.teacher_name}</TableCell>
                <TableCell>{session.subject}</TableCell>
                <TableCell>{session.grade}</TableCell>
                <TableCell>{formatDuration(session.duration)}</TableCell>
                <TableCell>{session.views}</TableCell>
                <TableCell>
                  <Chip
                    label={session.is_published ? 'Published' : 'Draft'}
                    color={session.is_published ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip label={`${session.concepts_count} concepts`} size="small" />
                    <Chip label={`${session.resources_count} resources`} size="small" />
                    <Chip label={`${session.tests_count} tests`} size="small" />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(session)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(session.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingSession ? 'Edit Session' : 'Add New Session'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e: any) => setFormData({ ...formData, title: e.target.value })}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e: any) => setFormData({ ...formData, description: e.target.value })}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Teacher Name"
                value={formData.teacher_name}
                onChange={(e: any) => setFormData({ ...formData, teacher_name: e.target.value })}
              />

              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={formData.subject}
                  onChange={(e: any) => setFormData({ ...formData, subject: e.target.value })}>
                  <MenuItem value="Physics">Physics</MenuItem>
                  <MenuItem value="Chemistry">Chemistry</MenuItem>
                  <MenuItem value="Mathematics">Mathematics</MenuItem>
                  <MenuItem value="Biology">Biology</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Grade</InputLabel>
                <Select
                  value={formData.grade}
                  onChange={(e: any) => setFormData({ ...formData, grade: e.target.value })}>
                  <MenuItem value="11th">11th</MenuItem>
                  <MenuItem value="12th">12th</MenuItem>
                  <MenuItem value="1st PU">1st PU</MenuItem>
                  <MenuItem value="2nd PU">2nd PU</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Board</InputLabel>
                <Select
                  value={formData.board}
                  onChange={(e: any) => setFormData({ ...formData, board: e.target.value })}>
                  <MenuItem value="CBSE">CBSE</MenuItem>
                  <MenuItem value="State Board">State Board</MenuItem>
                  <MenuItem value="ICSE">ICSE</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Vimeo Video ID"
                value={formData.vimeo_video_id}
                onChange={(e: any) => setFormData({ ...formData, vimeo_video_id: e.target.value })}
                helperText="Enter the Vimeo video ID (numbers only)"
                sx={{ flex: 2 }}
              />

              <TextField
                fullWidth
                type="number"
                label="Duration (seconds)"
                value={formData.duration}
                onChange={(e: any) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                sx={{ flex: 1 }}
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Thumbnail
              </Typography>
              {formData.thumbnail_url ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {thumbnailPreview ? (
                    <Box component="img" src={thumbnailPreview} alt="Thumbnail"
                      sx={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0' }} />
                  ) : (
                    <Box sx={{ width: 120, height: 68, borderRadius: 1, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                  <Button size="small" component="label" startIcon={<CloudUploadIcon />} disabled={thumbnailUploading}>
                    Replace
                    <input type="file" hidden accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => e.target.files?.[0] && uploadThumbnail(e.target.files[0])} />
                  </Button>
                  <IconButton size="small" onClick={() => { setFormData({ ...formData, thumbnail_url: '' }); setThumbnailPreview(''); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button variant="outlined" component="label" startIcon={thumbnailUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />} disabled={thumbnailUploading}>
                  {thumbnailUploading ? 'Uploading…' : 'Upload Thumbnail'}
                  <input type="file" hidden accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => e.target.files?.[0] && uploadThumbnail(e.target.files[0])} />
                </Button>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_published}
                  onChange={(e: any) => setFormData({ ...formData, is_published: e.target.checked })}
                />
              }
              label="Publish immediately"
            />

            {/* Concepts Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Concepts Covered</Typography>
                <Button size="small" onClick={handleAddConcept}>
                  Add Concept
                </Button>
              </Box>
              {formData.concepts.map((concept, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Concept Title"
                        value={concept.title}
                        onChange={(e: any) => {
                          const newConcepts = [...formData.concepts];
                          newConcepts[index].title = e.target.value;
                          setFormData({ ...formData, concepts: newConcepts });
                        }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Description"
                        value={concept.description}
                        onChange={(e: any) => {
                          const newConcepts = [...formData.concepts];
                          newConcepts[index].description = e.target.value;
                          setFormData({ ...formData, concepts: newConcepts });
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Resources Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Resources</Typography>
                <Button size="small" onClick={handleAddResource}>
                  Add Resource
                </Button>
              </Box>
              {formData.resources.map((resource, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Resource Title"
                          value={resource.title}
                          onChange={(e: any) => {
                            const newResources = [...formData.resources];
                            newResources[index].title = e.target.value;
                            setFormData({ ...formData, resources: newResources });
                          }}
                        />
                        <FormControl fullWidth>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={resource.type}
                            onChange={(e: any) => {
                              const newResources = [...formData.resources];
                              newResources[index] = { ...newResources[index], type: e.target.value, url: '' };
                              setFormData({ ...formData, resources: newResources });
                              setResourcePreviews(prev => { const next = { ...prev }; delete next[index]; return next; });
                            }}>
                            <MenuItem value="pdf">PDF</MenuItem>
                            <MenuItem value="link">Link</MenuItem>
                            <MenuItem value="document">Document</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      {resource.type === 'link' ? (
                        <TextField
                          fullWidth
                          label="URL"
                          placeholder="https://..."
                          value={resource.url}
                          onChange={(e: any) => {
                            const newResources = [...formData.resources];
                            newResources[index] = { ...newResources[index], url: e.target.value };
                            setFormData({ ...formData, resources: newResources });
                          }}
                        />
                      ) : resource.url ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={resourcePreviews[index] ? 'File attached' : 'Loading…'}
                            color="success" size="small"
                            onClick={resourcePreviews[index] ? () => window.open(resourcePreviews[index], '_blank') : undefined}
                          />
                          <Button size="small" component="label" startIcon={<CloudUploadIcon />} disabled={!!resourceUploading[index]}>
                            Replace
                            <input type="file" hidden accept="application/pdf"
                              onChange={(e) => e.target.files?.[0] && uploadResourceFile(index, e.target.files[0])} />
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined" size="small" component="label"
                          startIcon={resourceUploading[index] ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                          disabled={!!resourceUploading[index]}>
                          {resourceUploading[index] ? 'Uploading…' : 'Upload File'}
                          <input type="file" hidden accept="application/pdf"
                            onChange={(e) => e.target.files?.[0] && uploadResourceFile(index, e.target.files[0])} />
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingSession ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RecordedSessionsPage;
